import axios from "axios";
import { message } from "antd";
import { isShiftEscapeUrl, outsideShiftError, withinShiftNow } from "../lib/userShift";
import i18nModule from "../../i18";

const t = (key, fallback) => i18nModule.i18n.t(key, fallback);

const API_BASE_URL_RAW =
  process.env.REACT_APP_API_URL || "https://alwaa.skysourcesolution.com/api";

export const API_BASE_URL = API_BASE_URL_RAW.replace(/\/$/, "");
/** Same base as the API; uploads resolve to `${STORAGE_BASE_URL}/storage/...` (e.g. …/api/storage/settings/…). */
export const STORAGE_BASE_URL = API_BASE_URL.replace(/\/$/, "");

// Create an axios instance with the base URL
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // cut hung/slow requests so the UI never spins forever
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
});

// Function to get the full API URL for a specific endpoint
export const getApiUrl = (endpoint) => {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  return `${API_BASE_URL}/${cleanEndpoint}`;
};

// Build absolute URL for files served from /storage.
export const getStorageUrl = (filePath) => {
  if (!filePath) return "";
  let cleanPath = String(filePath).replace(/^\/+/, "").replace(/\\/g, "/");
  if (cleanPath.startsWith("storage/")) {
    cleanPath = cleanPath.slice("storage/".length);
  }
  return `${STORAGE_BASE_URL}/storage/${cleanPath}`;
};

// Add a request interceptor to handle authentication tokens + shift lock
apiClient.interceptors.request.use(
  (config) => {
    const raw = localStorage.getItem("sessionToken");
    const token = typeof raw === "string" ? raw.trim() : "";
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const method = String(config.method || "get").toUpperCase();
    const isWrite = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
    if (isWrite && !config.skipShiftCheck && !withinShiftNow()) {
      const url = config.url || "";
      if (!isShiftEscapeUrl(url)) {
        const err = outsideShiftError();
        message.error(err.message);
        return Promise.reject(err);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const SESSION_EXPIRED_EVENT = "velan:session-expired";

// Add a response interceptor for centralized error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Aborted / superseded requests are normal (date filter changes, unmount) — don't log as errors.
    const canceled =
      error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError" ||
      error?.name === "AbortError" ||
      String(error?.message || "").toLowerCase() === "canceled";
    if (canceled) {
      return Promise.reject(error);
    }

    const skipAuthRedirect = Boolean(error?.config?.skipAuthRedirect);
    if (error.response) {
      const { status, data } = error.response;
      if (error.code === "OUTSIDE_SHIFT" || data?.code === "OUTSIDE_SHIFT") {
        // Toast already shown by request interceptor (or show once from API).
        if (error.code !== "OUTSIDE_SHIFT" && !error?.config?.skipForbiddenToast) {
          message.error(data?.message || "Outside your working hours.");
        }
      } else if (status === 401 && !skipAuthRedirect) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      } else if (status === 403 && !error?.config?.skipForbiddenToast) {
        message.error(data?.message || "You do not have permission to perform this action.");
      }
      console.error(`API Error (${status}):`, data);
    } else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      console.error("API Error: Request timed out", error.config?.url);
      if (!error?.config?.skipTimeoutToast) {
        message.error(t("requestTimeoutError", "Request took too long — check your internet connection and try again."));
      }
    } else if (error.request) {
      console.error('API Error: No response received', error.request);
    } else if (error?.code === "OUTSIDE_SHIFT") {
      // Already toasted in the request interceptor.
    } else {
      console.error('API Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API helper functions
export const API_BASEURL = async () => {
  try {
    const res = await apiClient.get('/');
    return res.data;
  } catch (error) {
    console.error("Error fetching API base data:", error);
    throw error;
  }
};

export const fetchAnalyticsData = async () => {
  try {
    const res = await apiClient.get('/reports');
    return res.data;
  } catch (error) {
    console.error("Error fetching analytics data:", error);
    throw error;
  }
};
